import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Icon, Spinner, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
const IMPACT_FAILED_TOAST = {
    type: 'error',
    title: 'Archive failed',
    subtitle: 'Could not check the archive impact across study environments. Try again later.',
};
const ARCHIVE_FAILED_SUBTITLE = 'Something went wrong while archiving. Try again later.';
const BLOCKED_REASON_MESSAGES = {
    environment_unreachable: {
        one: "Archival unavailable because {environments} with this template assigned to it is unavailable. Please ensure there's no active deployment and retry.",
        many: "Archival unavailable because {environments} with this template assigned to them are unavailable. Please ensure there's no active deployment and retry.",
    },
    no_cortex_access: {
        one: "This consent template is assigned to {environments} and your user doesn't have access to make changes to that environment. Update your access for that environment and retry.",
        many: "This consent template is assigned to {environments} and your user doesn't have access to make changes to those environments. Update your access for those environments and retry.",
    },
};
const environmentLabel = (translate, { id, name, isProd }) => {
    const values = { name: name ?? `#${id}` };
    if (isProd === undefined) {
        return translate('Environment {name}', values);
    }
    return isProd ? translate('Prod Environment {name}', values) : translate('Test Environment {name}', values);
};
// Axios messages ("Request failed with status code 502") are not useful to Studio users.
const archiveErrorSubtitle = (error) => {
    if (error instanceof Error && error.message && !error.isAxiosError) {
        return error.message;
    }
    return ARCHIVE_FAILED_SUBTITLE;
};
const FIGMA = {
    width: 805,
    radius: 16,
    shadow: '0px 14px 44px 0px rgba(0, 0, 0, 0.24)',
    scrim: 'rgba(0, 0, 0, 0.5)',
    text: '#111827',
    muted: '#6B7280',
    border: '#E5E7EB',
    wash: '#F9FAFB',
    voided: '#B71C1C',
    completed: '#166640',
    headerPadding: '28px 36px 24px 52px',
    bodyPadding: '24px 44px 20px',
    footerPadding: '18px 36px 18px 44px',
    impactPadding: '14px 3px',
    impactRadius: 10,
    separatorHeight: 57,
};
const overlayStyle = {
    position: 'fixed',
    inset: 0,
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
};
const scrimStyle = {
    position: 'absolute',
    inset: 0,
    backgroundColor: FIGMA.scrim,
};
const panelStyle = {
    position: 'relative',
    display: 'flex',
    width: '100%',
    maxWidth: FIGMA.width,
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    borderRadius: FIGMA.radius,
    boxShadow: FIGMA.shadow,
};
const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: FIGMA.headerPadding,
    borderBottom: `1px solid ${FIGMA.border}`,
};
const titleStyle = {
    margin: 0,
    flex: 1,
    color: FIGMA.text,
    fontSize: 24,
    fontWeight: 600,
    lineHeight: 1.2,
};
const closeButtonStyle = {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    margin: 0,
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: FIGMA.muted,
    cursor: 'pointer',
};
const bodyStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: FIGMA.bodyPadding,
};
const copyStyle = {
    margin: 0,
    color: FIGMA.text,
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '28px',
};
const helperStyle = {
    margin: 0,
    color: FIGMA.text,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '24px',
};
const impactCardStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: FIGMA.impactPadding,
    backgroundColor: FIGMA.wash,
    border: `1px solid ${FIGMA.border}`,
    borderRadius: FIGMA.impactRadius,
};
const statColumnStyle = {
    display: 'flex',
    minWidth: 0,
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    textAlign: 'center',
};
const statValueStyle = (color) => ({
    margin: 0,
    color,
    fontSize: 26,
    fontWeight: 600,
    lineHeight: 1.15,
});
const statLabelStyle = {
    margin: 0,
    color: FIGMA.text,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '24px',
};
const separatorStyle = {
    width: 1,
    height: FIGMA.separatorHeight,
    flexShrink: 0,
    backgroundColor: FIGMA.border,
};
const footerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 14,
    padding: FIGMA.footerPadding,
    backgroundColor: FIGMA.wash,
    borderTop: `1px solid ${FIGMA.border}`,
    borderBottomLeftRadius: FIGMA.radius,
    borderBottomRightRadius: FIGMA.radius,
};
const ImpactStat = ({ value, label, color, dataTestId, showDivider }) => (_jsxs(_Fragment, { children: [_jsxs("div", { style: statColumnStyle, children: [_jsx("p", { style: statValueStyle(color), "data-testid": dataTestId, children: value }), _jsx("p", { style: statLabelStyle, children: label })] }), showDivider ? _jsx("div", { "aria-hidden": 'true', style: separatorStyle }) : null] }));
export const ConfirmArchivalModal = ({ isOpen, resourceId, templateKey, getArchiveImpact, onConfirm, onClose, }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const [impactState, setImpactState] = useState({ status: 'idle' });
    const [isConfirming, setIsConfirming] = useState(false);
    const confirmInFlightRef = useRef(false);
    const panelRef = useRef(null);
    const getArchiveImpactRef = useRef(getArchiveImpact);
    getArchiveImpactRef.current = getArchiveImpact;
    const addNotificationRef = useRef(addNotification);
    addNotificationRef.current = addNotification;
    const translateRef = useRef(translate);
    translateRef.current = translate;
    useEffect(() => {
        if (!isOpen) {
            setImpactState({ status: 'idle' });
            setIsConfirming(false);
            confirmInFlightRef.current = false;
            return;
        }
        let cancelled = false;
        setImpactState({ status: 'loading' });
        getArchiveImpactRef
            .current(templateKey, resourceId)
            .then((impact) => {
            if (!cancelled) {
                setImpactState({ status: 'ready', impact });
            }
        })
            .catch(() => {
            if (!cancelled) {
                setImpactState({ status: 'error' });
                addNotificationRef.current({
                    type: IMPACT_FAILED_TOAST.type,
                    title: translateRef.current(IMPACT_FAILED_TOAST.title),
                    subtitle: translateRef.current(IMPACT_FAILED_TOAST.subtitle),
                });
            }
        });
        return () => {
            cancelled = true;
        };
    }, [isOpen, resourceId, templateKey]);
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        panelRef.current?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isOpen]);
    const handleClose = () => {
        if (isConfirming) {
            return;
        }
        onClose();
    };
    const handleArchive = async () => {
        if (confirmInFlightRef.current) {
            return;
        }
        confirmInFlightRef.current = true;
        setIsConfirming(true);
        let impactChecked = false;
        try {
            const impact = await getArchiveImpact(templateKey, resourceId);
            impactChecked = true;
            setImpactState({ status: 'ready', impact });
            if (!impact.canArchive) {
                return;
            }
            await onConfirm();
            onClose();
        }
        catch (error) {
            setImpactState({ status: 'error' });
            addNotification({
                type: IMPACT_FAILED_TOAST.type,
                title: translate(IMPACT_FAILED_TOAST.title),
                subtitle: impactChecked ? translate(archiveErrorSubtitle(error)) : translate(IMPACT_FAILED_TOAST.subtitle),
            });
        }
        finally {
            confirmInFlightRef.current = false;
            setIsConfirming(false);
        }
    };
    if (!isOpen) {
        return null;
    }
    const canArchive = impactState.status === 'ready' && impactState.impact.canArchive;
    const showArchiveButton = impactState.status !== 'error' && (impactState.status !== 'ready' || canArchive);
    const archiveDisabled = !canArchive || isConfirming;
    const impact = impactState.status === 'ready' ? impactState.impact : undefined;
    const statValue = (value) => value ?? '—';
    const blockedReason = impact && !impact.canArchive ? impact.blockedReason : undefined;
    const environmentList = (environments) => environments.map((environment) => environmentLabel(translate, environment)).join(', ');
    const blockedEnvironments = blockedReason
        ? (impact?.blockedEnvironments ?? []).filter((environment) => environment.reason === blockedReason)
        : [];
    const blockedMessage = blockedReason
        ? translate(BLOCKED_REASON_MESSAGES[blockedReason][blockedEnvironments.length > 1 ? 'many' : 'one'], {
            environments: environmentList(blockedEnvironments),
        })
        : undefined;
    const pausedEnvironments = (impact?.skippedEnvironments ?? []).filter((environment) => environment.reason === 'paused');
    return createPortal(_jsxs("div", { style: overlayStyle, children: [_jsx("div", { style: scrimStyle, "aria-hidden": 'true', "data-testid": 'confirm-archival-scrim' }), _jsxs("div", { ref: panelRef, role: 'dialog', "aria-modal": 'true', "aria-labelledby": 'confirm-archival-title', tabIndex: -1, "data-testid": 'confirm-archival-modal', style: panelStyle, onKeyDown: (event) => {
                    if (event.key === 'Escape') {
                        event.stopPropagation();
                        handleClose();
                    }
                }, children: [_jsxs("div", { style: headerStyle, children: [_jsx("h2", { id: 'confirm-archival-title', style: titleStyle, children: translate('Confirm Archival') }), _jsx("button", { type: 'button', "aria-label": translate('Close'), "data-testid": 'consent-archive-dismiss', disabled: isConfirming, onClick: handleClose, style: closeButtonStyle, children: _jsx(Icon, { name: 'close', size: 22, color: 'grayDark' }) })] }), _jsxs("div", { style: bodyStyle, children: [_jsx("p", { style: copyStyle, children: translate('Review the information below before archiving this template. This cannot be undone. Archiving voids every pending document created from this version, they can no longer be signed, including any already with a signer. The template can no longer be restored, edited, or published.') }), impactState.status === 'loading' ? (_jsx("div", { style: { ...impactCardStyle, justifyContent: 'center', minHeight: FIGMA.separatorHeight + 28 }, children: _jsx(Spinner, {}) })) : (_jsxs("div", { style: impactCardStyle, children: [_jsx(ImpactStat, { value: statValue(impact?.documentsWillBeVoided), label: translate('Documents will be voided'), color: FIGMA.voided, dataTestId: 'consent-archive-impact-voided', showDivider: true }), _jsx(ImpactStat, { value: statValue(impact?.sitesAffected), label: translate('Sites affected'), color: FIGMA.text, dataTestId: 'consent-archive-impact-sites', showDivider: true }), _jsx(ImpactStat, { value: statValue(impact?.completedKept), label: translate('Completed, kept'), color: FIGMA.completed, dataTestId: 'consent-archive-impact-completed' })] })), _jsx("p", { style: helperStyle, children: translate('Completed documents stay available to sites and participants.') }), blockedMessage ? (_jsx("p", { role: 'alert', style: { ...helperStyle, color: FIGMA.voided }, "data-testid": 'consent-archive-blocked-reason', children: blockedMessage })) : null, pausedEnvironments.length ? (_jsx("p", { style: { ...helperStyle, color: FIGMA.muted }, "data-testid": 'consent-archive-paused-environments', children: translate('Paused environments are not included: {environments}. They will pick up the archive when they are resumed.', { environments: environmentList(pausedEnvironments) }) })) : null] }), _jsxs("div", { style: footerStyle, children: [_jsx(Button, { label: translate('Cancel'), dataTestId: 'consent-archive-cancel', variant: 'transparent', disabled: isConfirming, onClick: handleClose }), showArchiveButton ? (_jsx(Button, { label: translate('Archive'), dataTestId: 'consent-archive-confirm', variant: 'primary', disabled: archiveDisabled, onClick: () => void handleArchive() })) : null] })] })] }), document.body);
};
